import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Compass,
  HandHeart,
  ShieldStar,
  Rocket,
} from "@phosphor-icons/react";

const teamImage =
  "https://images.unsplash.com/photo-1681949287382-052ea3954a51?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxzdGFydHVwJTIwdGVhbSUyMG1vZGVybiUyMG9mZmljZXxlbnwwfHx8fDE3ODEwNzcyMzF8MA&ixlib=rb-4.1.0&q=85";

const timeline = [
  { label: "Founded", value: "Mar 2026" },
  { label: "Brilliant minds", value: "50" },
  { label: "Continents served", value: "4" },
  { label: "Uptime SLA", value: "99.999%" },
];

const pillars = [
  {
    icon: ShieldStar,
    title: "Operational Excellence",
    desc: "We measure ourselves in p99.9 latencies and zero-surprise quarters.",
  },
  {
    icon: HandHeart,
    title: "Customer Obsession",
    desc: "Senior architects own customer outcomes end-to-end. No relay teams.",
  },
  {
    icon: Compass,
    title: "Security First",
    desc: "Default-deny, identity-bound, cryptographically attested — by design.",
  },
  {
    icon: Rocket,
    title: "Bias for Velocity",
    desc: "We ship the smallest correct thing today, not the perfect thing next quarter.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="border-b border-zinc-200 relative overflow-hidden">
        <div className="absolute inset-0 grid-noise opacity-50 pointer-events-none" />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32 relative">
          <div className="eyebrow mb-6">Our Mission</div>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-display text-6xl sm:text-7xl lg:text-[120px] leading-[0.9] tracking-tight"
          >
            We Build
            <br />
            <span className="text-blue-700">the Grid.</span>
          </motion.h1>
          <p className="mt-10 text-lg lg:text-xl text-zinc-600 max-w-2xl leading-relaxed">
            At Infranova Solutions, our mission is to innovate infrastructure
            — so you can focus on building the future.
          </p>
        </div>
      </section>

      {/* TIMELINE STATS */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="mx-auto max-w-[1400px] grid grid-cols-2 lg:grid-cols-4">
          {timeline.map((t, i) => (
            <div
              key={t.label}
              data-testid={`about-stat-${i}`}
              className={`p-8 lg:p-12 border-zinc-200 ${i < timeline.length - 1 ? "lg:border-r" : ""} ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b lg:border-b-0" : ""}`}
            >
              <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                {t.label}
              </div>
              <div className="mt-3 font-display text-4xl lg:text-5xl tracking-tight">
                {t.value}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* NARRATIVE */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="eyebrow mb-6">The Infranova Story</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Founded to fix a fractured stack.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-8 text-zinc-700 text-lg leading-relaxed">
            <p>
              In early 2026, the tech landscape was fragmented. Enterprise
              infrastructure was caught between bloated legacy providers and
              fragile startup tools.{" "}
              <span className="text-zinc-950 font-medium">
                Infranova Solutions was launched in March 2026 to bridge that
                exact gap.
              </span>
            </p>
            <p>
              We started with a singular mission: to provide highly
              professional, secure, and forward-thinking cloud infrastructure
              that balances the raw agility of a startup with the bulletproof
              reliability of an enterprise partner.
            </p>
            <p>
              Today, our team has grown to{" "}
              <span className="text-zinc-950 font-medium">
                50 brilliant minds
              </span>{" "}
              — architects, site reliability engineers, and security experts.
              We aren&apos;t just scaling our company; we&apos;re scaling the standard
              for what modern infrastructure should be.
            </p>
          </div>
        </div>
      </section>

      {/* TEAM IMAGE */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
          <div className="relative aspect-[16/9] lg:aspect-[21/9] border border-zinc-200 overflow-hidden">
            <img
              src={teamImage}
              alt="Infranova team collaborating"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 bg-white/95 border-t border-r border-zinc-200 p-6 lg:p-8 max-w-md">
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Engineering · 2026
              </div>
              <div className="mt-2 font-display text-2xl lg:text-3xl tracking-tight">
                Architects, SREs, security specialists — under one roof.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CULTURE PILLARS */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
          <div className="eyebrow mb-6">Culture</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-3xl">
            Precision in every packet.
          </h2>
          <p className="mt-6 text-lg text-zinc-600 max-w-2xl leading-relaxed">
            Our culture is rooted in absolute operational excellence. We
            believe in shipping robust, scalable, beautifully architected
            systems — and being radically honest with the people who depend
            on them.
          </p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t border-zinc-200 bg-white">
            {pillars.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="border-r border-b border-zinc-200 p-8 lg:p-10"
                >
                  <Icon size={26} weight="duotone" className="text-blue-700" />
                  <h3 className="mt-6 font-display text-xl tracking-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-zinc-950 text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-blue-400 mb-6">
              // Join us
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Want to build the grid with us?
            </h2>
            <p className="mt-5 text-lg text-zinc-300 max-w-xl">
              We&apos;re hiring senior engineers across distributed systems,
              security, and SRE.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/contact"
              data-testid="about-careers-cta"
              className="inline-flex items-center gap-2 bg-blue-700 text-white px-7 py-4 text-base font-medium hover:bg-blue-600 transition-colors w-fit"
            >
              View open roles
              <ArrowUpRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
